import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import App from "./App.tsx";
import "./index.css";

// Tag <html> with the native platform so CSS can opt-in iOS-only treatments
// (e.g. the Home screen Liquid Glass background). Capacitor's getPlatform()
// returns "ios" | "android" | "web".
(async () => {
  try {
    const { Capacitor } = await import("@capacitor/core");
    const platform = Capacitor.getPlatform();
    if (platform) document.documentElement.setAttribute("data-platform", platform);
    if (platform === "ios") {
      const { installIOSBounceGuard } = await import("./lib/disableIOSBounce");
      installIOSBounceGuard();
    }
  } catch {
    // Capacitor not available (e.g. plain web build) — leave attribute unset.
  }
})();


declare const __APP_RELEASE__: string;

// ── Deferred Sentry init ──────────────────────────────────────────────
// Sentry adds ~243KB to the critical path. We defer init until the browser
// is idle (or 3s after first paint at the latest) so it never blocks TTI.
//
// Errors and unhandled rejections that happen BEFORE init are queued and
// flushed once Sentry loads, so we don't lose any crash visibility during
// the deferral window.
type QueuedEvent =
  | { kind: "error"; error: unknown }
  | { kind: "rejection"; reason: unknown };

const preInitQueue: QueuedEvent[] = [];
let sentryReady = false;

const earlyErrorHandler = (event: ErrorEvent) => {
  try {
    const err = event.error ?? new Error(event.message);
    posthog.captureException(err, { $exception_source: "global_error_handler" });
  } catch { /* never let telemetry crash the app */ }
  if (sentryReady) return;
  preInitQueue.push({ kind: "error", error: event.error ?? event.message });
};
const earlyRejectionHandler = (event: PromiseRejectionEvent) => {
  // Keep the existing IndexedDB swallow behaviour so suspended-page errors
  // don't flood Sentry as unhandled rejections.
  const reason = event.reason as { name?: string; message?: string } | undefined;
  if (
    reason?.name === "InvalidStateError" &&
    typeof reason?.message === "string" &&
    (reason.message.includes("IDBDatabase") ||
      reason.message.includes("database connection is closing"))
  ) {
    event.preventDefault();
    return;
  }
  try {
    const err = event.reason instanceof Error
      ? event.reason
      : new Error(typeof event.reason === "string" ? event.reason : "Unhandled promise rejection");
    posthog.captureException(err, { $exception_source: "unhandled_rejection" });
  } catch { /* swallow */ }
  if (sentryReady) return;
  preInitQueue.push({ kind: "rejection", reason: event.reason });
};

window.addEventListener("error", earlyErrorHandler);
window.addEventListener("unhandledrejection", earlyRejectionHandler);

const initSentry = async () => {
  if (sentryReady) return;
  try {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn: "https://5ae41fc04911440ff32bb7ea615958ca@o4511270584516608.ingest.de.sentry.io/4511270585892945",
      environment: import.meta.env.MODE,
      release: typeof __APP_RELEASE__ !== "undefined" ? __APP_RELEASE__ : "dev",
      enabled: import.meta.env.PROD,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
    });
    sentryReady = true;
    // Flush anything captured before init.
    for (const evt of preInitQueue) {
      try {
        if (evt.kind === "error") Sentry.captureException(evt.error);
        else Sentry.captureException(evt.reason);
      } catch {
        // Swallow — flushing must never crash the app.
      }
    }
    preInitQueue.length = 0;
  } catch (err) {
    // If Sentry itself fails to load, don't crash the app.
    console.warn("[Sentry] deferred init failed", err);
  }
};

// Schedule init for the next idle window, with a hard 3s fallback so it
// always runs even if the main thread stays busy.
const scheduleSentry = () => {
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === "function") {
    ric(() => { void initSentry(); }, { timeout: 2000 });
  } else {
    setTimeout(() => { void initSentry(); }, 2000);
  }
};

if (document.readyState === "complete") {
  scheduleSentry();
} else {
  window.addEventListener("load", scheduleSentry, { once: true });
}

// Re-export a tiny shim so other modules can still call Sentry.withProfiler
// during their own evaluation. App.tsx imports Sentry directly for that
// HOC — that import path is unchanged below.

const EXERCISE_CACHE_BUST_KEY = "exercise_cache_bust_v2";

function bustStaleExerciseMediaCaches() {
  try {
    if (localStorage.getItem(EXERCISE_CACHE_BUST_KEY) === "1") return;

    localStorage.removeItem("cached_exercises");

    const staleKeys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith("exercise_gif_v4_")) {
        staleKeys.push(key);
      }
    }

    staleKeys.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem(EXERCISE_CACHE_BUST_KEY, "1");
  } catch {
    // Storage can be unavailable in private mode; cache busting is best-effort.
  }
}

bustStaleExerciseMediaCaches();

posthog.init("phc_yqp4Bcw32RFVXFZzm8zTW5X7AdA4VGkSnnwFqWZUCERj", {
  api_host: "https://t.mypocketcoach.app",
  ui_host: "https://eu.posthog.com",
  loaded: (ph) => {
    if (import.meta.env.DEV) ph.opt_out_capturing();
  },
  capture_pageview: "history_change",
  capture_pageleave: true,
  persistence: "localStorage",
  autocapture: true,
  disable_compression: true,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: '*',
    // Block ALL media from replays. Progress photos, body-analysis result
    // screens and any canvas-rendered imagery must never reach a recording.
    blockSelector: 'img, video, canvas, svg, picture, source, iframe, [style*="background-image"]',
    maskCapturedNetworkRequestFn: null,
    collectFonts: false,
  },

});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  </HelmetProvider>
);





// Always tear down any previously-registered service workers + caches.
// We don't ship a /sw.js anymore; old installs were serving stale HTML
// shells that pointed at deleted hashed chunks, causing "Importing a
// module script failed" and "Script ... failed to load" errors in Sentry.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
}
if ("caches" in window) {
  caches
    .keys()
    .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
    .catch(() => {});
}

// Auto-recover from stale chunk references (e.g. user kept a tab open
// across a deploy and a lazy route now points at a deleted bundle).
// Hard-reload once per session so the browser fetches the fresh shell.
const CHUNK_RELOAD_KEY = "__chunk_reload_attempted__";
const looksLikeChunkLoadError = (message: string | undefined) => {
  if (!message) return false;
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    (message.includes("Loading chunk") && message.includes("failed"))
  );
};
const tryRecoverFromChunkError = (message: string | undefined) => {
  if (!looksLikeChunkLoadError(message)) return;
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1") return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch {
    // sessionStorage can be unavailable; reload anyway as a last resort.
  }
  window.location.reload();
};
window.addEventListener("error", (event) => {
  tryRecoverFromChunkError(event.message ?? (event.error as Error | undefined)?.message);
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as { message?: string } | undefined;
  tryRecoverFromChunkError(reason?.message);
});
